import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Table, 
  Button, 
  Form, 
  InputGroup,
  Modal,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Badge
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import * as Icons from 'react-icons/fa';
import Icon from '../components/Icon';
import { servicesAPI } from '../services/api';
import { AxiosResponse } from 'axios';

interface Service {
  _id: string;
  name: string;
  description: string;
  images: {
    path: string;
    secure_url: string;
    public_id: string;
    isMain: boolean;
    _id: string;
  }[];
  catalog: {
    file: string;
    fileName?: string;
    fileType?: string;
    description?: string;
  };
  createdAt: string;
  updatedAt?: string;
}

// Define an extended API interface to include downloadServiceCatalog
interface ServicesAPIExtended {
  getServices: (params?: any) => Promise<AxiosResponse>;
  getServiceById: (id: string) => Promise<AxiosResponse>;
  createService: (formData: FormData) => Promise<AxiosResponse>;
  updateService: (id: string, formData: FormData) => Promise<AxiosResponse>;
  deleteService: (id: string) => Promise<AxiosResponse>;
  deleteServiceImage: (serviceId: string, imageId: string) => Promise<AxiosResponse>;
  setMainServiceImage: (serviceId: string, imageId: string) => Promise<AxiosResponse>;
  downloadServiceCatalog: (serviceId: string) => Promise<AxiosResponse>;
}

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState<Service | null>(null);
  const navigate = useNavigate();

  // Fetch services on component mount
  useEffect(() => {
    fetchServices();
  }, []);

  // Function to fetch services from API
  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await servicesAPI.getServices();
      setServices(response.data);
    } catch (err: any) {
      setError('Failed to fetch services');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Filter services based on search term
  const filteredServices = services.filter(service => 
    (service.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (service.description?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // Get the most recent date (either creation or update)
  const getActionDate = (service: Service) => {
    if (service.updatedAt) {
      return formatDate(service.updatedAt);
    }
    return formatDate(service.createdAt);
  };

  // Confirm service deletion
  const confirmDelete = (service: Service) => {
    setServiceToDelete(service);
    setShowDeleteModal(true);
  };

  // Handle service deletion
  const handleDelete = async () => {
    if (!serviceToDelete) return;
    
    try {
      await servicesAPI.deleteService(serviceToDelete._id);
      setServices(services.filter(s => s._id !== serviceToDelete._id));
      setShowDeleteModal(false);
      setServiceToDelete(null);
    } catch (err: any) {
      setError('Failed to delete service');
      console.error(err);
    }
  };

  // Download catalog function
  const handleDownloadCatalog = async (serviceId: string) => {
    try {
      // Get service details first to verify catalog exists
      const serviceResponse = await servicesAPI.getServiceById(serviceId);
      const service = serviceResponse.data;
      
      if (!service.catalog || !service.catalog.file) {
        setError('Catalog file not available');
        return;
      }
      
      // Cast the API to our extended type to resolve the linter error
      const typedAPI = servicesAPI as unknown as ServicesAPIExtended;
      const response = await typedAPI.downloadServiceCatalog(serviceId);
      
      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'service-catalog.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError('Failed to download catalog');
      console.error(err);
    }
  };

  if (loading && services.length === 0) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading services...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1 className="mb-0">Services</h1>
        </Col>
        <Col xs="auto">
          <Link to="/services/new">
            <Button variant="primary">
              <Icon icon={Icons.FaPlus} className="me-2" /> Add Service
            </Button>
          </Link>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">All Services</h5>
            </Col>
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <Icon icon={Icons.FaSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover striped className="mb-0">
            <thead className="bg-light">
              <tr>
                <th style={{ width: '60px' }}>Image</th>
                <th>Title</th>
                <th>Description</th>
                <th>Created At</th>
                <th>Last Action</th>
                <th>Catalog</th>
                <th style={{ width: '150px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.length > 0 ? (
                filteredServices.map((service) => (
                  <tr key={service._id}>
                    <td>
                      {service.images && service.images.length > 0 ? (
                        <img 
                          src={`${service.images[0].secure_url}`} 
                          alt={service.name} 
                          style={{ width: '50px', height: '50px', objectFit: 'cover' }} 
                        />
                      ) : (
                        <div 
                          className="bg-light d-flex align-items-center justify-content-center" 
                          style={{ width: '50px', height: '50px' }}
                        >
                          <Icon icon={Icons.FaEye} />
                        </div>
                      )}
                    </td>
                    <td>{service.name || 'Untitled'}</td>
                    <td>
                      {service.description?.length > 50
                        ? `${service.description.substring(0, 50)}...`
                        : service.description || 'No description'}
                    </td>
                    <td>{formatDate(service.createdAt)}</td>
                    <td>
                      <span className="d-flex align-items-center">
                        <Icon icon={Icons.FaClock} className="me-1 text-muted" /> 
                        {getActionDate(service)}
                      </span>
                    </td>
                    <td>
                      {service.catalog && service.catalog.file ? (
                        <a 
                          href={`${process.env.REACT_APP_API_URL_WITHOUT_API}/${service.catalog.file}`} 
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          <Icon icon={Icons.FaFilePdf} className="me-1" /> View
                        </a>
                      ) : (
                        <span className="text-muted">No catalog</span>
                      )}
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        <OverlayTrigger overlay={<Tooltip>Edit Service</Tooltip>}>
                          <Button 
                            variant="primary" 
                            size="sm"
                            onClick={() => navigate(`/services/edit/${service._id}`)}
                            className="me-2"
                          >
                            <Icon icon={Icons.FaEdit} />
                          </Button>
                        </OverlayTrigger>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Delete service</Tooltip>}
                        >
                          <Button 
                            variant="outline-danger"
                            onClick={() => confirmDelete(service)}
                          >
                            <Icon icon={Icons.FaTrash} />
                          </Button>
                        </OverlayTrigger>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-3">
                    {error ? error : 'No services found'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the service "{serviceToDelete?.name}"? 
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Services; 